import { useEffect } from 'react';

import { useStudentGenerateMessageMutation, type IncludeKey } from '~/services/studentData';
import { studentDataStore } from '~/store/studentData/studentDataStore';
import { useActiveStudentContext } from './useActiveStudentContext';

import { GenerateMessagePanel } from './GenerateMessagePanel';
import { MessagePanel } from './MessagePanel';

export function CustomMessagesTab() {
  const { studentId, periodId, periodCreatedAt } = useActiveStudentContext();

  const timeframe = studentDataStore((s) => s.timeframe);
  const setTimeframe = studentDataStore((s) => s.setTimeframe);

  const draftInclude = studentDataStore((s) => s.draftInclude);
  const setDraftInclude = studentDataStore((s) => s.setDraftInclude);
  const draftFormat = studentDataStore((s) => s.draftFormat);
  const setDraftFormat = studentDataStore((s) => s.setDraftFormat);
  const draftTimeframe = studentDataStore((s) => s.draftTimeframe);
  const setDraftTimeframe = studentDataStore((s) => s.setDraftTimeframe);

  const appliedInclude = studentDataStore((s) => s.appliedInclude);
  const setAppliedInclude = studentDataStore((s) => s.setAppliedInclude);
  const appliedFormat = studentDataStore((s) => s.appliedFormat);
  const setAppliedFormat = studentDataStore((s) => s.setAppliedFormat);

  const { mutate: generateMessage, message, status: messageStatus } = useStudentGenerateMessageMutation();

  // Auto-generate on mount and when student changes, using the applied options
  useEffect(() => {
    if (!studentId) return;
    const includeArr = (Object.keys(appliedInclude) as IncludeKey[]).filter((k) => appliedInclude[k]);
    generateMessage({
      studentId,
      periodId,
      periodCreatedAt,
      timeframe,
      include: includeArr,
      format: appliedFormat,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const isDirty =
    JSON.stringify(draftInclude) !== JSON.stringify(appliedInclude) ||
    draftTimeframe !== timeframe ||
    draftFormat !== appliedFormat;

  const hasAnyInclude = Object.values(draftInclude).some(Boolean);

  const handleIncludeChange = (key: IncludeKey, checked: boolean) => {
    setDraftInclude({ ...draftInclude, [key]: checked });
  };

  const handleGenerate = () => {
    setAppliedInclude(draftInclude);
    setAppliedFormat(draftFormat);
    setTimeframe(draftTimeframe);

    const includeArr = (Object.keys(draftInclude) as IncludeKey[]).filter((k) => draftInclude[k]);
    generateMessage({
      studentId,
      periodId,
      periodCreatedAt,
      timeframe: draftTimeframe,
      include: includeArr,
      format: draftFormat,
    });
  };

  return (
    <>
      <GenerateMessagePanel
        includeOptions={draftInclude}
        timeframe={draftTimeframe}
        format={draftFormat}
        isDirty={isDirty}
        hasAnyInclude={hasAnyInclude}
        onIncludeChange={handleIncludeChange}
        onTimeframeChange={setDraftTimeframe}
        onFormatChange={setDraftFormat}
        onGenerate={handleGenerate}
      />
      <MessagePanel
        included={appliedInclude}
        timeframe={timeframe}
        format={appliedFormat}
        message={message}
        isLoading={messageStatus === 'pending'}
        isError={messageStatus === 'error'}
      />
    </>
  );
}
